import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { CalendarIcon, HomeIcon } from 'lucide-react';
import { DevicesIcon } from '@/assets/devices-icon.tsx';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { DEVICE_TYPES_NAMES_ARRAY } from '@/features/devices/schemas/devices.schemas.ts';
import { EventRuleFormData } from '@/features/event-rules/schemas/create-event-rules.schema.ts';

export function CreateEventRuleBasicInformationForm() {
  const form = useFormContext<EventRuleFormData>();
  const watchRuleType = form.watch('ruleType');

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-semibold">Basic Information</h2>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="eventTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">Event Title</FormLabel>
              <FormControl>
                <Input placeholder="High Battery Temperature" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ruleType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">Event Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="plant">
                    <div className="flex items-center gap-2">
                      <HomeIcon size={16} className="text-muted-foreground" />
                      Plant
                    </div>
                  </SelectItem>
                  <SelectItem value="device">
                    <div className="flex items-center gap-2">
                      <DevicesIcon height="16" width="16" className="text-muted-foreground" />
                      Device
                    </div>
                  </SelectItem>
                  <SelectItem value="schedule">
                    <div className="flex items-center gap-2">
                      <CalendarIcon size={16} className="text-muted-foreground" />
                      Schedule
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <PlantNameSelectFormEventRules />
      {watchRuleType === 'device' && <DeviceSelectFormEventRules />}
    </div>
  );
}

function DeviceSelectFormEventRules() {
  const form = useFormContext<EventRuleFormData>();
  const watchDeviceType = form.watch('deviceType');
  const watchSelectedPlantId = form.watch('plantId');

  const { data: devices, isLoading: arePlantDevicesLoading } = useGetPlantDevicesQuery({
    plantId: watchSelectedPlantId,
    enabled: Boolean(watchSelectedPlantId),
  });

  const availableDeviceTypes = useMemo(
    () => DEVICE_TYPES_NAMES_ARRAY.filter((dt) => devices?.find((d) => d.type === dt)),
    [devices]
  );

  const availableDevicesForDeviceType = useMemo(
    () => devices?.filter((d) => watchDeviceType === d.type),
    [devices, watchDeviceType]
  );

  const isDeviceTypeFieldEnabled = !watchSelectedPlantId || arePlantDevicesLoading;

  const isDeviceSerialNumberFieldEnabled =
    !watchSelectedPlantId || !form.watch('deviceType') || arePlantDevicesLoading;

  return (
    <div className="grid grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name="deviceType"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Device Type</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                form.setValue('conditions', [{ fact: '', operator: 'equal', value: '' }]);
              }}
              value={field.value}
              disabled={isDeviceTypeFieldEnabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      arePlantDevicesLoading ? 'Loading devices...' : 'Select device type'
                    }
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {availableDeviceTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="deviceId"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Device Serial Number</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);

                const deviceSN = availableDevicesForDeviceType?.find(
                  (d) => d.deviceId === value
                )?.sn;

                if (deviceSN) {
                  form.setValue('deviceSN', deviceSN);
                }
              }}
              value={field.value}
              disabled={isDeviceSerialNumberFieldEnabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      arePlantDevicesLoading
                        ? 'Loading devices...'
                        : 'Select device serial number'
                    }
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {availableDevicesForDeviceType?.length ? (
                  availableDevicesForDeviceType?.map((device) => (
                    <SelectItem key={device.deviceId} value={device.deviceId}>
                      {device.sn}
                    </SelectItem>
                  ))
                ) : (
                  <div className="flex flex-col px-1 text-sm">
                    <span className="">No devices found.</span>
                  </div>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function PlantNameSelectFormEventRules() {
  const form = useFormContext<EventRuleFormData>();

  const { data: availablePlants, isLoading } = useGetPlantsQuery({ includeOverview: false });

  return (
    <FormField
      control={form.control}
      name="plantId"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="required">Plant Name</FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
            }}
            value={field.value}
            disabled={isLoading}
          >
            <FormControl>
              <SelectTrigger className="mt-1">
                <SelectValue
                  placeholder={isLoading ? 'Loading plants...' : 'Select plant name'}
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {availablePlants?.map((plant) => (
                <SelectItem key={plant.plantId} value={plant.plantId}>
                  {plant.basicDetails.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
